import React, { useEffect, useRef, useState } from "react";
import { connect, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Switch,
  FormControlLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Search as SearchIcon, Close, Add } from "@mui/icons-material";
import moment from "moment";
import CommunityAdminService, {
  Room,
  RoomFormData,
} from "../../services/CommunityAdminService.ts";

type View = "list" | "edit";

const RoomsManager: React.FC<{ user?: any }> = ({ user }) => {
  const [view, setView] = useState<View>("list");
  const [rooms, setRooms] = useState<Room[]>([]);
  const [total, setTotal] = useState(0);
  const [listSearch, setListSearch] = useState("");
  const [listLoading, setListLoading] = useState(false);

  const [editing, setEditing] = useState<Room | null>(null);
  const [formData, setFormData] = useState<RoomFormData>({
    name: "",
    description: "",
    icon: "",
    banner: "",
    isOfficial: false,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [iconUploading, setIconUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const iconRef = useRef<HTMLInputElement>(null);
  const bannerRef = useRef<HTMLInputElement>(null);

  const isPermission =
    (user?.role === "staff" && user?.permission?.p_staff > 1) ||
    user?.role !== "staff";

  const fetchRooms = async (search = "") => {
    setListLoading(true);
    try {
      const res = await CommunityAdminService.listRooms(search, 1, 50);
      if (res.status) {
        setRooms(res.data);
        setTotal(res.pagination.total);
      }
    } catch {
      toast.error("Failed to load rooms");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const openNew = () => {
    setEditing(null);
    setFormData({
      name: "",
      description: "",
      icon: "",
      banner: "",
      isOfficial: false,
    });
    setErrors({});
    setView("edit");
  };

  const openEdit = (room: Room) => {
    setEditing(room);
    setFormData({
      name: room.name,
      description: room.description || "",
      icon: room.icon || "",
      banner: room.banner || "",
      isOfficial: room.isOfficial,
    });
    setErrors({});
    setView("edit");
  };

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "icon" | "banner",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const setUploading =
      field === "icon" ? setIconUploading : setBannerUploading;
    setUploading(true);
    try {
      const result = await CommunityAdminService.uploadImage(file);
      if (result.status)
        setFormData((prev) => ({ ...prev, [field]: result.imageUrl }));
      else toast.error("Upload failed");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (field === "icon" && iconRef.current) iconRef.current.value = "";
      if (field === "banner" && bannerRef.current) bannerRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      setErrors({ name: "Room name is required" });
      return;
    }
    setSaving(true);
    try {
      const res = editing
        ? await CommunityAdminService.updateRoom(editing._id, formData)
        : await CommunityAdminService.createRoom(formData);

      if (res.status) {
        toast.success(res.message);
        setView("list");
        fetchRooms(listSearch);
      } else {
        toast.error(res.error || "Failed to save room");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to save room");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (room: Room) => {
    if (!window.confirm(`Delete "${room.name}"? This cannot be undone.`))
      return;
    try {
      const res = await CommunityAdminService.deleteRoom(room._id);
      if (res.status) {
        toast.success("Room deleted");
        fetchRooms(listSearch);
      } else {
        toast.error(res.error);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to delete room");
    }
  };

  // ── Edit view ──────────────────────────────────────────────────────────────
  if (view === "edit") {
    return (
      <div className="container-fluid" style={{ maxWidth: 720, paddingTop: 8 }}>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h3 className="fw-bold mb-0">{editing ? "Edit Room" : "New Room"}</h3>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => setView("list")}
              style={{
                padding: "7px 16px",
                border: "2px solid #000",
                borderRadius: 8,
                background: "#fff",
                fontWeight: 700,
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isPermission || saving}
              style={{
                padding: "7px 20px",
                border: "2px solid #000",
                borderRadius: 8,
                background: "#FFD600",
                fontWeight: 700,
                fontSize: "0.85rem",
                cursor: isPermission ? "pointer" : "default",
                opacity: isPermission ? 1 : 0.5,
              }}
            >
              {saving ? "Saving…" : "Save Room"}
            </button>
          </div>
        </div>

        <div
          style={{
            border: "1.5px solid #e0e0e0",
            borderRadius: 10,
            background: "#fff",
            padding: 20,
          }}
        >
          <div className="mb-3">
            <label className="fw-600 small mb-1 d-block">Name *</label>
            <input
              className="form-control"
              placeholder="e.g. Arcade Cabs"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              disabled={!isPermission}
            />
            {errors.name && <div className="error_msg">{errors.name}</div>}
          </div>

          <div className="mb-3">
            <label className="fw-600 small mb-1 d-block">Description</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="What's this room about?"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              disabled={!isPermission}
            />
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="fw-600 small mb-1 d-block">Icon</label>
              <div
                style={{
                  width: 90,
                  height: 90,
                  borderRadius: 12,
                  background: "#f5f5f5",
                  border: "1.5px dashed #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  position: "relative",
                  marginBottom: 8,
                }}
              >
                {formData.icon ? (
                  <>
                    <img
                      src={formData.icon}
                      alt="Icon"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    {isPermission && (
                      <button
                        onClick={() => setFormData({ ...formData, icon: "" })}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          background: "rgba(0,0,0,0.6)",
                          border: "none",
                          borderRadius: "50%",
                          width: 22,
                          height: 22,
                          color: "#fff",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Close style={{ fontSize: 13 }} />
                      </button>
                    )}
                  </>
                ) : (
                  <span style={{ fontSize: "0.68rem", color: "#bbb" }}>
                    No icon
                  </span>
                )}
              </div>
              <input
                type="file"
                ref={iconRef}
                className="d-none"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, "icon")}
              />
              <button
                className="btn btn-sm"
                style={{
                  background: "#FFD600",
                  fontWeight: 700,
                  border: "2px solid #000",
                }}
                onClick={() => iconRef.current?.click()}
                disabled={!isPermission || iconUploading}
              >
                {iconUploading ? "Uploading…" : "Upload Icon"}
              </button>
            </div>

            <div className="col-md-6">
              <label className="fw-600 small mb-1 d-block">Banner</label>
              <div
                style={{
                  width: "100%",
                  aspectRatio: "16/9",
                  borderRadius: 8,
                  background: "#f5f5f5",
                  border: "1.5px dashed #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  position: "relative",
                  marginBottom: 8,
                }}
              >
                {formData.banner ? (
                  <>
                    <img
                      src={formData.banner}
                      alt="Banner"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    {isPermission && (
                      <button
                        onClick={() => setFormData({ ...formData, banner: "" })}
                        style={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          background: "rgba(0,0,0,0.6)",
                          border: "none",
                          borderRadius: "50%",
                          width: 24,
                          height: 24,
                          color: "#fff",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Close style={{ fontSize: 14 }} />
                      </button>
                    )}
                  </>
                ) : (
                  <span style={{ fontSize: "0.72rem", color: "#bbb" }}>
                    No banner
                  </span>
                )}
              </div>
              <input
                type="file"
                ref={bannerRef}
                className="d-none"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, "banner")}
              />
              <button
                className="btn btn-sm"
                style={{
                  background: "#FFD600",
                  fontWeight: 700,
                  border: "2px solid #000",
                }}
                onClick={() => bannerRef.current?.click()}
                disabled={!isPermission || bannerUploading}
              >
                {bannerUploading ? "Uploading…" : "Upload Banner"}
              </button>
            </div>
          </div>

          <FormControlLabel
            control={
              <Switch
                checked={!!formData.isOfficial}
                onChange={(e) =>
                  setFormData({ ...formData, isOfficial: e.target.checked })
                }
                disabled={!isPermission}
              />
            }
            label={
              <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                Official room
              </span>
            }
          />
        </div>
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid" style={{ maxWidth: 960, paddingTop: 8 }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold mb-0">
          Rooms <span className="text-muted fs-5 fw-normal">({total})</span>
        </h3>
        <button
          onClick={openNew}
          disabled={!isPermission}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "7px 18px",
            border: "2px solid #000",
            borderRadius: 8,
            background: "#FFD600",
            fontWeight: 700,
            fontSize: "0.85rem",
            cursor: isPermission ? "pointer" : "default",
            opacity: isPermission ? 1 : 0.5,
          }}
        >
          <Add fontSize="small" /> New Room
        </button>
      </div>

      <TextField
        fullWidth
        size="small"
        placeholder="Search rooms…"
        value={listSearch}
        onChange={(e) => {
          setListSearch(e.target.value);
          fetchRooms(e.target.value);
        }}
        className="mb-4"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {listLoading ? (
        <div className="text-center py-5">Loading…</div>
      ) : rooms.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover user-management-table">
            <thead>
              <tr>
                <th>Icon</th>
                <th>Name</th>
                <th>Members</th>
                <th>Type</th>
                <th>Created</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((room) => (
                <tr key={room._id}>
                  <td>
                    {room.icon ? (
                      <img
                        src={room.icon}
                        alt={room.name}
                        style={{
                          width: 36,
                          height: 36,
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          background: "#eee",
                          borderRadius: 8,
                        }}
                      />
                    )}
                  </td>
                  <td>
                    <div className="fw-600">{room.name}</div>
                    {room.description && (
                      <div className="text-muted small">
                        {room.description.slice(0, 60)}
                        {room.description.length > 60 ? "…" : ""}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="badge bg-light text-dark border">
                      {room.membersCount || 0}
                    </span>
                  </td>
                  <td>
                    {room.isOfficial ? (
                      <span
                        className="badge"
                        style={{ background: "#FFD600", color: "#000" }}
                      >
                        Official
                      </span>
                    ) : (
                      <span className="badge bg-light text-dark border">
                        Community
                      </span>
                    )}
                  </td>
                  <td className="small text-muted">
                    {moment(room.createdAt).format("MMM D, YYYY")}
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-sm btn-link p-0 me-3 text-primary fw-bold"
                      onClick={() => openEdit(room)}
                    >
                      Edit
                    </button>
                    {isPermission && (
                      <button
                        className="btn btn-sm btn-link p-0 text-danger fw-bold"
                        onClick={() => handleDelete(room)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <p className="fw-bold">No rooms yet</p>
          <p className="small">
            Create the first community room to get started.
          </p>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({ user: state.auth.user });
export default connect(mapStateToProps)(RoomsManager);
